import React, { ReactElement } from "react";
import { StepProps } from "../TradeModal";
import { useQueryBundle } from "source/hooks";
import LoadingBlock from "components/Loading/LoadingBlock";
import AccountCard from "components/AccountCard/AccountCard";
import styles from "./SelectAccount.module.scss";
import { GetInstitutionLoginsWithHolding } from "$gql/queries/general/GetInstitutionLoginsWithHolding.gen";
import Button from "components/core/Button/Button";

const renderNoSupportedAccountsView = (hasConnectedAccountsBefore: boolean) => {
  return (
    <div>
      <p className={styles.mainMessage}>
        {hasConnectedAccountsBefore &&
          "You currently don’t have a Tradier or E-Trade account connected to your TiiCKER account. "}
        Please feel free to make trades on your existing accounts, or you can
        connect your Tradier or E-Trade account on your{" "}
        <a href="/my-account">My Account </a>page. If you don’t already have
        one,{" "}
        <a href="https://onboarding.tradier.com/signup" target="_blank">
          click here
        </a>{" "}
        to create a free commission-free Tradier account.
      </p>
      <Button
        href="/dashboard"
        data-cy="visit-my-stocks"
        className={styles.button}
      >
        Go to My Account
      </Button>
    </div>
  );
};

export default function SelectAccount(props: StepProps): ReactElement {
  props.setTitle("Select Account");

  const accountQuery = useQueryBundle(GetInstitutionLoginsWithHolding, {
    variables: {
      stockSymbol: props.state.tickerSymbol,
    },
  });

  if (accountQuery.state !== "DONE") {
    return <LoadingBlock />;
  }

  if (accountQuery.data.loggedInUser?.institutionLogins.length === 0) {
    return renderNoSupportedAccountsView(false);
  }

  if (!accountQuery.data.loggedInUser) {
    props.dispatch({ type: "signedOut" });
    return <></>;
  }

  const allAccounts = accountQuery.data.loggedInUser.institutionLogins.map(
    (login) => {
      const institutionAccounts = login.institutionAccounts.map((account) => {
        return {
          account,
          totalHoldings: account.stockDetails.sharesOwned,
          totalValue: account.stockDetails.totalValue,
        };
      });

      return {
        ...login,
        institutionAccounts,
      };
    }
  );

  const availableAccountsToTrade = allAccounts.filter(
    (a) =>
      a.institutionName === "Tradier" ||
      a.institutionName === "TD Ameritrade" ||
      a.institutionName.includes("E*TRADE")
  );

  // We opted to not show these accounts, this is kept just in case this
  // decision is reversed

  // const accountsThatCannotTrade = allAccounts.filter(
  //   a => !availableAccountsToTrade.map(a => a.id).includes(a.id)
  // );

  if (props.state.institutionAccountId) {
    const selectedLogin = availableAccountsToTrade.find((login) => {
      return login.institutionAccounts.find((account) => {
        return (
          account.account.id.toString() ===
          props.state.institutionAccountId?.toString()
        );
      });
    });

    if (!selectedLogin) {
      throw new Error(
        [
          `No accounts found matching the preloaded account id of [${props.state.institutionAccountId}].`,
          `The preloaded account was not found or is not able to trade.`,
        ].join(" ")
      );
    }

    const selectedAccount = selectedLogin.institutionAccounts.find(
      (account) => {
        return account.account.id === props.state.institutionAccountId;
      }
    );

    props.dispatch({
      type: "selectedAccount",
      payload: {
        brokerName: selectedLogin.institutionName,
        institutionAccountId: props.state.institutionAccountId,
        institutionLoginId: selectedLogin.id,
        buyingPower: selectedAccount?.account.cashValue || 0,
        accountNumber: selectedAccount?.account.mask || "",
        availableShares: selectedAccount?.totalHoldings || 0,
      },
    });

    return <LoadingBlock />;
  }

  if (availableAccountsToTrade.length === 0) {
    return renderNoSupportedAccountsView(true);
  }

  return (
    <div>
      {availableAccountsToTrade.map((login) =>
        login.institutionAccounts.map((account) => (
          <AccountCard
            key={account.account.id}
            onClick={() =>
              props.dispatch({
                type: "selectedAccount",
                payload: {
                  brokerName: login.institutionName,
                  institutionAccountId: account.account.id,
                  institutionLoginId: login.id,
                  buyingPower: account.account.cashValue || 0,
                  accountNumber: account.account.mask || "",
                  availableShares: account.totalHoldings,
                },
              })
            }
            brokerName={login.institutionName}
            buyingPower={account.account.cashValue || 0}
            accountMask={account.account.mask || "****"}
            accountName={account.account.name || ""}
            tickerSymbol={props.state.tickerSymbol}
            shareCount={account.totalHoldings}
            shareValue={account.totalValue}
            isActive={true}
          />
        ))
      )}

      {/* See note above about not displaying un-supported accounts */}
      {/* {accountsThatCannotTrade.map(login =>
        login.institutionAccounts.map(account => (
          <AccountCard
            key={account.account.id}
            brokerName={login.institutionName}
            buyingPower={account.account.allCash || 0}
            accountMask={account.account.mask || "****"}
            accountName={account.account.name || ""}
            tickerSymbol={props.state.tickerSymbol}
            shareCount={account.totalHoldings}
            shareValue={account.totalValue}
            isActive={false}
          />
        ))
      )} */}
    </div>
  );
}
